import React from "react";
import {
  Card, CardContent, Box, Stack, Typography, TextField,
  IconButton, CircularProgress, Chip, Divider,
} from "@mui/material";
import { MapPin, Flag, ArrowRight, RefreshCw, Bus, Car } from "lucide-react";

/**
 * Status line shown under an address once the backend has resolved it.
 * Module-scope so its identity is stable across renders.
 */
const EndpointStatus = ({ info, kind }) => {
  // No detection yet (before first fetch) — reserve space to avoid jump.
  if (!info) return <Box sx={{ minHeight: 22 }} />;

  if (info.isStation) {
    return (
      <Chip
        size="small"
        icon={<Bus size={13} />}
        label="Linja-autoasema"
        color="success"
        variant="outlined"
        sx={{ height: 22, "& .MuiChip-label": { px: 0.75, fontSize: "0.72rem", fontWeight: 600 } }}
      />
    );
  }

  const km = typeof info.distanceToStation === "number" ? info.distanceToStation.toFixed(1) : "?";
  const needsCar = kind === "origin" ? info.needsFirstMile : info.needsLastMile;

  if (needsCar) {
    const text =
      kind === "origin"
        ? `Arvioitu lähin asema: ${info.nearestStation} (~${km} km)`
        : `Arvioitu lähin asema: ${info.nearestStation} (~${km} km)`;
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minHeight: 22 }}>
        <Car size={13} style={{ flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, lineHeight: 1.2 }} title={text}>
          {text}
        </Typography>
      </Stack>
    );
  }

  // Not a station and no car leg (e.g. too far from any station).
  return (
    <Typography variant="caption" sx={{ color: "text.secondary", minHeight: 22, display: "block" }}>
      Lähin asema: {info.nearestStation} (~{km} km)
    </Typography>
  );
};

/**
 * One editable endpoint (address field + detected status).
 * MUST live at module scope — defining it inside RouteTimeHeader would give it
 * a new identity every render and the TextField would lose focus on each keystroke.
 */
const AddressEndpoint = ({ icon, label, address, onAddressChange, info, kind, loading }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
      {icon}
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "text.secondary" }}
      >
        {label}
      </Typography>
    </Stack>
    <TextField
      value={address}
      onChange={(e) => onAddressChange(e.target.value)}
      size="small"
      fullWidth
      disabled={loading}
      placeholder="Osoite tai asema"
      sx={{
        mb: 0.75,
        bgcolor: "background.paper",
        borderRadius: 1.5,
        "& input": { fontSize: { xs: "0.85rem", sm: "0.875rem" }, fontWeight: 600 },
        "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
      }}
    />
    <EndpointStatus info={info} kind={kind} />
  </Box>
);

/**
 * Route + time header for the bus transport modal.
 *
 * Addresses are editable free text; the parent owns their value (it needs them
 * to fetch). After a fetch the backend reports how each endpoint resolved via
 * originInfo / destinationInfo, which we display read-only. Wrong detection →
 * the user edits the address and refreshes.
 */
export const RouteTimeHeader = ({
  origin,
  destination,
  onOriginChange,
  onDestinationChange,
  originInfo,
  destinationInfo,
  selectedTime,
  onTimeChange,
  onRefresh,
  loading,
  isMobile,
}) => {
  const nowMin = new Date().toISOString().slice(0, 16);

  return (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, bgcolor: "primary.50" }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        {/* Endpoints with detected status */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1.5, sm: 2 }}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
        >
          <AddressEndpoint
            icon={<MapPin size={15} />}
            label="Lähtö"
            address={origin}
            onAddressChange={onOriginChange}
            info={originInfo}
            kind="origin"
            loading={loading}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pt: { sm: 3.5 },
              color: "text.disabled",
            }}
          >
            <ArrowRight size={18} style={{ transform: isMobile ? "rotate(90deg)" : "none" }} />
          </Box>

          <AddressEndpoint
            icon={<Flag size={15} />}
            label="Määränpää"
            address={destination}
            onAddressChange={onDestinationChange}
            info={destinationInfo}
            kind="destination"
            loading={loading}
          />
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Departure time + refresh */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "stretch", sm: "flex-end" }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: "text.secondary", whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}
          >
            Lähtöaika
          </Typography>
          <TextField
            type="datetime-local"
            size="small"
            value={selectedTime}
            onChange={(e) => onTimeChange(e.target.value)}
            inputProps={{ min: nowMin }}
            sx={{
              bgcolor: "background.paper",
              borderRadius: 1.5,
              flex: { xs: 1, sm: "0 0 auto" },
              width: { sm: 220 },
              "& input": { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
            }}
          />
          <IconButton
            onClick={onRefresh}
            disabled={loading}
            size="small"
            aria-label="Päivitä"
            sx={{
              bgcolor: "primary.main",
              color: "white",
              borderRadius: 1.5,
              "&:hover": { bgcolor: "primary.dark" },
              "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
            }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : <RefreshCw size={18} />}
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
};